import type { TranslationKey } from '../i18n';
import type {
  FulfillmentAcceptance,
  FulfillmentErrorKey,
  FulfillmentMethod,
  FulfillmentQuote,
} from '../types/store-fulfillment';
import type { DeliveryMethod, OrderStatus } from '../types/order';

export const MAX_FEE_CENTS = 100000;
export const PICKUP_ACTIVE_LIMIT = 1;

/** Blank disables delivery; invalid precision/sign/amount is never rounded. */
export function parseDeliveryFeeCents(value: string): number | null {
  const text = value.trim();
  if (text === '') return null;
  if (!/^\d+(?:\.\d{1,2})?$/.test(text)) throw new Error('FULFILLMENT_CONFIG_INVALID');
  const [whole = '', fraction = ''] = text.split('.');
  const cents = BigInt(whole) * 100n + BigInt(fraction.padEnd(2, '0'));
  if (cents > BigInt(MAX_FEE_CENTS)) throw new Error('AMOUNT_OUT_OF_RANGE');
  return Number(cents);
}

export function getOrderFulfillmentMethod(order: {
  readonly delivery_method: DeliveryMethod;
}): FulfillmentMethod {
  return order.delivery_method === 'pickup' ? 'pickup' : 'delivery';
}

export function buildFulfillmentAcceptance(quote: FulfillmentQuote): FulfillmentAcceptance {
  return {
    version: quote.version,
    revision: quote.revision,
    selection: quote.selection,
    merchandiseFingerprint: quote.merchandiseFingerprint,
    subtotalCents: quote.subtotalCents,
    feeCents: quote.feeCents,
    totalCents: quote.totalCents,
    currency: quote.currency,
  };
}

export interface OrderAmountDisplay {
  readonly amountCents: number;
  readonly confirmed: boolean;
  readonly labelKey: 'checkout.total' | 'fulfillment.legacySubtotal';
}

export function getOrderAmountDisplay(order: {
  readonly subtotal_cents: number;
  readonly total_cents: number | null;
}): OrderAmountDisplay {
  return order.total_cents !== null
    ? { amountCents: order.total_cents, confirmed: true, labelKey: 'checkout.total' }
    : {
        amountCents: order.subtotal_cents,
        confirmed: false,
        labelKey: 'fulfillment.legacySubtotal',
      };
}

export function getOrderStatusTranslationKey(
  status: OrderStatus,
  method: FulfillmentMethod = 'delivery',
): TranslationKey {
  return status === 'delivered' && method === 'pickup'
    ? 'fulfillment.collected'
    : `order.status.${status}`;
}

export function mapFulfillmentError(code: string | null | undefined): FulfillmentErrorKey | null {
  // Merchandise failures keep their customer or prepared-order recovery and message context.
  switch (code) {
    case 'FULFILLMENT_BUSY':
      return 'fulfillment.busy';
    case 'FULFILLMENT_UNAVAILABLE':
    case 'FULFILLMENT_OPTION_UNAVAILABLE':
      return 'fulfillment.unavailable';
    case 'FULFILLMENT_QUOTE_CHANGED':
    case 'FULFILLMENT_ACCEPTANCE_INVALID':
      return 'fulfillment.optionsChanged';
    case 'FULFILLMENT_CLIENT_UPDATE_REQUIRED':
      return 'fulfillment.clientUpdateRequired';
    case 'AMOUNT_OUT_OF_RANGE':
      return 'fulfillment.amountOutOfRange';
    case 'DELIVERY_FULFILLMENT_ONLY':
      return 'fulfillment.deliveryOnly';
    case 'PICKUP_FULFILLMENT_ONLY':
      return 'fulfillment.pickupOnly';
    default:
      return null;
  }
}
